import React, { useEffect, useState } from 'react';
import { api } from '../api';
import Layout from '../components/Layout';

export default function CreativeGallery() {
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [deleting, setDeleting] = useState('');

  const pageSize = 10;
  const pageCount = Math.max(1, Math.ceil(creatives.length / pageSize));
  const visibleCreatives = creatives.slice((page - 1) * pageSize, page * pageSize);

  const loadCreatives = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api.getCreatives();
      const images = data.images || [];
      setCreatives(images);
      setPage((current) => Math.min(current, Math.max(1, Math.ceil(images.length / pageSize))));
    } catch (err) {
      console.error('Failed to load creative gallery:', err);
      setError(err.message || 'Unable to load creative gallery.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCreatives();
  }, []);

  const handleDelete = async (creative) => {
    const filename = creative.filename;
    if (!filename || deleting) return;

    const confirmed = window.confirm(
      `Delete "${filename}"${creative.media_type === "video" || creative.media_type === "audio" ? "" : " and its matching JSON metadata, if present"}? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      setDeleting(filename);
      setError('');
      await api.deleteCreative(filename);
      setCreatives((current) => {
        const next = current.filter((item) => item.filename !== filename);
        setPage((currentPage) => Math.min(
          currentPage,
          Math.max(1, Math.ceil(next.length / pageSize))
        ));
        return next;
      });
    } catch (err) {
      console.error('Failed to delete creative:', err);
      setError(err.message || `Unable to delete ${filename}.`);
    } finally {
      setDeleting('');
    }
  };

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Creative Gallery</h1>
            <p className="mt-1 text-sm text-gray-500">
              All permanently saved advertising creatives.
            </p>
          </div>
          <button
            type="button"
            onClick={loadCreatives}
            disabled={loading || Boolean(deleting)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="rounded-xl border border-gray-200 bg-white py-16 text-center text-sm text-gray-500 shadow-sm">
            Loading creative gallery...
          </div>
        ) : creatives.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center shadow-sm">
            <p className="text-sm font-medium text-gray-700">No creatives yet.</p>
            <p className="mt-1 text-sm text-gray-500">
              Generated advertisements will appear here permanently.
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
              <span>
                Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, creatives.length)} of {creatives.length} media files
              </span>
              <Pagination page={page} pageCount={pageCount} onPageChange={setPage} />
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {visibleCreatives.map((creative) => (
                <CreativeCard
                  key={creative.filename || creative.image_url}
                  creative={creative}
                  onDelete={handleDelete}
                  deleting={deleting === creative.filename}
                  deleteDisabled={Boolean(deleting)}
                />
              ))}
            </div>

            {pageCount > 1 && (
              <div className="flex justify-center pt-2">
                <Pagination page={page} pageCount={pageCount} onPageChange={setPage} />
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

function Pagination({ page, pageCount, onPageChange }) {
  if (pageCount <= 1) return null;

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => onPageChange((current) => Math.max(1, current - 1))}
        disabled={page <= 1}
        className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Previous
      </button>
      <span className="min-w-20 text-center text-sm font-medium text-gray-700">
        Page {page} of {pageCount}
      </span>
      <button
        type="button"
        onClick={() => onPageChange((current) => Math.min(pageCount, current + 1))}
        disabled={page >= pageCount}
        className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Next
      </button>
    </div>
  );
}

function CreativeCard({ creative, onDelete, deleting, deleteDisabled }) {
  const imageUrl = creative.image_url;
  const filename = creative.filename || 'creative.png';
  const facebookPost =
    creative.media_type !== 'video' && creative.media_type !== 'audio' &&
    typeof creative.facebook_post === 'string'
      ? creative.facebook_post.trim()
      : '';
  const [copyStatus, setCopyStatus] = useState('');

  const handleCopyFacebookPost = async () => {
    if (!facebookPost) {
      setCopyStatus('unavailable');
      return;
    }

    try {
      await navigator.clipboard.writeText(facebookPost);
      setCopyStatus('copied');
    } catch (err) {
      console.error('Failed to copy Facebook post:', err);
      setCopyStatus('failed');
    }
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      {creative.media_type === 'video' ? (
        <video
          src={imageUrl}
          controls
          preload="metadata"
          className="aspect-[4/5] w-full bg-black object-contain"
          aria-label={filename}
        />
      ) : creative.media_type === 'audio' ? (
        <div className="flex aspect-[4/5] flex-col items-center justify-center gap-4 bg-gray-100 p-4">
          <div className="text-sm font-medium text-gray-700">Audio file</div>
          <div className="max-w-full truncate text-xs text-gray-500">{filename}</div>
          <audio src={imageUrl} controls preload="metadata" className="w-full" />
        </div>
      ) : (
        <a href={imageUrl} target="_blank" rel="noreferrer">
          <img
            src={imageUrl}
            alt={filename}
            className="aspect-[4/5] w-full object-cover transition hover:opacity-90"
          />
        </a>
      )}

      <div className="p-4">
        <div className="truncate text-sm font-medium text-gray-800" title={filename}>
          {filename}
        </div>

        {creative.created_at && (
          <div className="mt-1 text-xs text-gray-500">{formatDate(creative.created_at)}</div>
        )}

        {creative.metadata?.landing_page_url && (
          <div className="mt-2 truncate text-xs text-gray-500" title={creative.metadata.landing_page_url}>
            {creative.metadata.landing_page_url}
          </div>
        )}

        <div className="mt-3 grid grid-cols-3 gap-2">
          {imageUrl ? (
            <a
              href={imageUrl}
              download
              target="_blank"
              rel="noreferrer"
              className="rounded-lg border border-gray-300 py-2 text-center text-sm font-medium text-gray-700 transition hover:bg-gray-50"
            >
              Download
            </a>
          ) : (
            <button
              type="button"
              disabled
              className="rounded-lg border border-gray-200 py-2 text-center text-sm font-medium text-gray-400"
            >
              Download
            </button>
          )}

          <button
            type="button"
            onClick={() => onDelete(creative)}
            disabled={deleteDisabled}
            className="rounded-lg border border-red-300 py-2 text-center text-sm font-medium text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
            title="Delete this media file and, for images, its matching JSON metadata"
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </button>

          {facebookPost ? (
            <button
              type="button"
              onClick={handleCopyFacebookPost}
              className="rounded-lg border border-gray-300 py-2 text-center text-sm font-medium text-gray-700 transition hover:bg-gray-50"
              title="Copy the saved Facebook post"
            >
              {copyStatus === 'copied'
                ? 'Copied!'
                : copyStatus === 'failed'
                  ? 'Retry Copy'
                  : 'Facebook Post'}
            </button>
          ) : (
            <button
              type="button"
              disabled
              title="This older creative has no saved Facebook post."
              className="cursor-not-allowed rounded-lg border border-gray-200 py-2 text-center text-sm font-medium text-gray-400"
            >
              Post Unavailable
            </button>
          )}
        </div>

        {copyStatus === 'failed' && facebookPost && (
          <p className="mt-2 text-xs text-red-600">
            Could not copy the post. Check browser clipboard permissions and retry.
          </p>
        )}
      </div>
    </div>
  );
}

function formatDate(value) {
  try {
    return new Date(value).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return value;
  }
}
